import React, { useCallback, useEffect, useState } from 'react';
import { permissionNotificationCourante, requestNotificationPermission } from '../utils/notifications';
import { isPushSupported, subscribeToPush } from '../services/pushSubscription';

/**
 * Invitation contextuelle à activer les notifications système.
 *
 * La permission n'est JAMAIS demandée au chargement. C'était le défaut précédent : `Dashboard` (supprime le 2026-09-20)
 * appelait `Notification.requestPermission()` dès son montage, avant que le chauffeur ait la
 * moindre idée de ce à quoi il consentait. Un navigateur mobile traite ce genre de demande
 * non sollicitée avec méfiance, et un refus est définitif — l'appareil devient alors
 * injoignable hors application, sans recours possible depuis le site.
 *
 * Ici, la demande part d'un geste explicite, après une phrase qui dit à quoi elle sert. C'est
 * aussi ce qu'exige iOS, qui n'accepte `requestPermission()` que depuis une interaction réelle.
 *
 * En cas de refus : plus aucune sollicitation. Le SSE continue de fonctionner normalement, et
 * la bannière disparaît définitivement.
 */
const InvitePermissionNotifications: React.FC = () => {
    const [permission, setPermission] = useState<NotificationPermission>('denied');
    const [supporte, setSupporte] = useState(false);
    const [enCours, setEnCours] = useState(false);

    useEffect(() => {
        const disponible = isPushSupported();
        setSupporte(disponible);
        if (disponible) {
            setPermission(permissionNotificationCourante());
        }
    }, []);

    // Permission déjà accordée lors d'une visite précédente : on (ré)enregistre l'appareil
    // sans rien demander. Le backend est idempotent, l'opération est donc sans effet de bord,
    // et elle rattrape un appareil qui aurait été révoqué à tort côté serveur.
    useEffect(() => {
        if (supporte && permission === 'granted') {
            subscribeToPush().catch(() => undefined);
        }
    }, [supporte, permission]);

    const activer = useCallback(async () => {
        setEnCours(true);
        try {
            const accordee = await requestNotificationPermission();
            setPermission(accordee);
            if (accordee === 'granted') {
                await subscribeToPush();
            }
        } catch {
            // Un échec d'abonnement ne casse rien : le temps réel SSE reste actif.
        } finally {
            setEnCours(false);
        }
    }, []);

    // Rien à proposer : appareil sans Push, permission déjà accordée, ou refus déjà exprimé.
    // Le refus ne rouvre jamais la bannière — insister est à la fois inutile et hostile.
    if (!supporte || permission !== 'default') {
        return null;
    }

    return (
        <div
            style={{
                background: '#1e293b', border: '1px solid #334155', borderRadius: 16,
                padding: 16, margin: '0 0 16px', display: 'flex', flexDirection: 'column', gap: 12,
            }}
        >
            <div style={{ color: '#f8fafc', fontSize: 15, fontWeight: 700 }}>
                Recevoir les nouvelles livraisons
            </div>
            <div style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.4 }}>
                Activez les notifications pour être averti d&apos;une nouvelle mission même lorsque
                l&apos;application est fermée.
            </div>
            <button
                type="button"
                onClick={activer}
                disabled={enCours}
                style={{
                    background: '#22c55e', border: 'none', borderRadius: 12, padding: '14px 20px',
                    color: '#000', fontSize: 16, fontWeight: 800,
                    cursor: enCours ? 'default' : 'pointer', opacity: enCours ? 0.6 : 1,
                }}
            >
                {enCours ? 'Activation...' : 'Activer les notifications'}
            </button>
        </div>
    );
};

export default InvitePermissionNotifications;
